/**
 * IconControl — Searchable icon picker for Lucide icons.
 * Uses Popover + Command (cmdk) for a combobox with search and grid preview.
 */

import { useState, useMemo } from 'react'
import { icons as lucideIcons, type LucideIcon } from 'lucide-react'
import { ChevronsUpDown } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

/** Curated icon names — common icons for storefronts and navigation */
const CURATED_ICONS = [
  // Arrows & navigation
  'chevron-right', 'chevron-left', 'chevron-down', 'chevron-up',
  'arrow-right', 'arrow-left', 'arrow-up', 'arrow-down',
  'arrow-up-right', 'arrow-down-right', 'move-right', 'external-link',
  // Actions
  'plus', 'minus', 'x', 'check', 'search', 'filter', 'settings',
  'edit', 'trash-2', 'copy', 'share-2', 'download', 'upload',
  // Commerce
  'shopping-cart', 'shopping-bag', 'package', 'truck', 'credit-card',
  'tag', 'percent', 'gift', 'star', 'heart', 'thumbs-up',
  // Communication
  'mail', 'phone', 'message-circle', 'message-square', 'bell',
  // Content
  'image', 'video', 'file-text', 'list', 'grid-3x3', 'layout-grid',
  // Social
  'globe', 'link', 'map-pin', 'calendar', 'clock', 'user',
  // Misc
  'eye', 'eye-off', 'info', 'alert-circle', 'zap', 'sparkles',
  'flame', 'bookmark', 'flag', 'award', 'crown', 'rocket',
] as const

/** Convert kebab-case to PascalCase for lucide-react icon lookup */
function toPascalCase(kebab: string): string {
  return kebab
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
}

/** Resolve a kebab-case icon name to a Lucide component */
function resolveIcon(name: string): LucideIcon | null {
  const pascal = toPascalCase(name)
  return (lucideIcons as Record<string, LucideIcon>)[pascal] ?? null
}

interface IconControlProps {
  value: string
  onChange: (v: string) => void
  label: string
}

export function IconControl({ value, onChange, label }: IconControlProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Build the icon list: curated first, then all lucide icons for search
  const allIcons = useMemo(() => {
    const curatedSet = new Set<string>(CURATED_ICONS)
    // Get all lucide icon names in kebab-case
    const allNames = Object.keys(lucideIcons)
      .filter((key) => {
        // Filter out non-icon exports (createLucideIcon, icons, etc.)
        const val = (lucideIcons as Record<string, unknown>)[key]
        return typeof val === 'object' && val !== null && 'displayName' in (val as Record<string, unknown>)
      })
      .map((pascal) =>
        pascal.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')
      )
    return { curated: [...CURATED_ICONS] as string[], all: allNames.filter((n) => !curatedSet.has(n)) }
  }, [])

  // Filter by search term
  const filtered = useMemo(() => {
    if (!search) return { curated: allIcons.curated, other: [] }
    const q = search.toLowerCase()
    return {
      curated: allIcons.curated.filter((n: string) => n.includes(q)),
      other: allIcons.all.filter((n: string) => n.includes(q)).slice(0, 30),
    }
  }, [search, allIcons])

  const CurrentIcon = resolveIcon(value)
  const hasResults = filtered.curated.length > 0 || filtered.other.length > 0

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm',
              'hover:bg-gray-50 transition-colors',
              !value && 'text-gray-400',
            )}
          >
            <span className="flex items-center gap-2 truncate">
              {CurrentIcon && <CurrentIcon className="h-4 w-4 shrink-0 text-gray-600" />}
              <span className="truncate">{value || 'Select icon...'}</span>
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search icons..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {!hasResults && <CommandEmpty>No icons found.</CommandEmpty>}
              {filtered.curated.length > 0 && (
                <CommandGroup heading={search ? 'Results' : 'Common'}>
                  <div className="grid grid-cols-6 gap-1 p-1">
                    {filtered.curated.map((name) => {
                      const Icon = resolveIcon(name)
                      if (!Icon) return null
                      const isSelected = value === name
                      return (
                        <button
                          key={name}
                          type="button"
                          title={name}
                          onClick={() => {
                            onChange(name)
                            setOpen(false)
                            setSearch('')
                          }}
                          className={cn(
                            'flex h-9 w-9 items-center justify-center rounded-xs transition-colors',
                            isSelected
                              ? 'bg-sky-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      )
                    })}
                  </div>
                </CommandGroup>
              )}
              {filtered.other.length > 0 && (
                <CommandGroup heading="More">
                  <div className="grid grid-cols-6 gap-1 p-1">
                    {filtered.other.map((name) => {
                      const Icon = resolveIcon(name)
                      if (!Icon) return null
                      const isSelected = value === name
                      return (
                        <button
                          key={name}
                          type="button"
                          title={name}
                          onClick={() => {
                            onChange(name)
                            setOpen(false)
                            setSearch('')
                          }}
                          className={cn(
                            'flex h-9 w-9 items-center justify-center rounded-xs transition-colors',
                            isSelected
                              ? 'bg-sky-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      )
                    })}
                  </div>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
